/**
 * Design System 2.0 tokens needed by the MUI side of the refresh.
 *
 * These duplicate values declared in `components/styles/design-system.css`, which is regrettable
 * but deliberate: Tailwind v4 tree-shakes `@theme` variables it cannot see used, so an Emotion rule
 * referencing `var(--color-light-2)` only resolves for as long as some Tailwind class happens to
 * use that token too. (Verified in a production build: `--color-light-2` is emitted because the
 * button recipes use it, `--color-light-5` is not.) A silent colour drop is a worse failure than a
 * duplicated hex, so the MUI side keeps its own copy.
 *
 * Retiring the flag deletes this file along with the rest of the directory.
 */

/** `Color/Light/0` — plain white, the top of the light ramp. */
export const LIGHT_0 = '#ffffff';

/** `Color/Light/2` — the DS 2.0 border that replaces elevation on light surfaces. */
export const LIGHT_2 = '#e3e5e8';

/** `Color/Light/5` — secondary text on a dark surface: legible without competing with `Light/0`. */
export const LIGHT_5 = '#acb0b9';

/** `Color/Light/Background` — the page behind every light surface. Legacy uses `grey[25]`, #fcfcfd. */
export const LIGHT_BACKGROUND = '#f9fafa';

/**
 * `Color/Dark/0` — the base of the dark surface ramp, and the background of every dark region.
 *
 * Legacy dark screens use `dark[300]` (#201f1f), a near-black grey. DS 2.0 goes to true black.
 */
export const DARK_0 = '#000000';

/**
 * `Color/Dark/3` — the panel sitting on `Dark/0`. The live-room sidebars are drawn at this step.
 */
export const DARK_3 = '#17191c';

/** `Color/Dark/4` — one step up from `Dark/3`: inputs and cards nested inside a dark panel. */
export const DARK_4 = '#23252a';

/** `Color/Dark/5` — the divider/border on a dark surface, the dark counterpart of `Light/2`. */
export const DARK_5 = '#2f3237';

/**
 * `Color/Dark/6` — a border meant to be SEEN rather than merely to separate, one step lighter than
 * `Dark/5`. The DS text input draws its own border here in the `.dark` scope.
 */
export const DARK_6 = '#474b52';

/** `Color/Dark/7` — the lightest of the dark ramp, used for disabled text and icons. */
export const DARK_7 = '#5f646d';

/**
 * `Color/Blue/Primary` — the DS 2.0 accent, the fill of a primary button (`.ds-btn-primary-default`).
 *
 * The legacy `palette.primary.main` is `blue[600]` (#1570ef), a softer, greener blue. Nothing passes
 * a colour into `theme()`, so despite the signature that value is a constant rather than per-account
 * branding — swapping it on a control is a straight colour change, not an override of someone's
 * brand.
 */
export const BLUE_PRIMARY = '#004fff';

/** `Color/Blue/2` — a pale blue, legible as an icon or accent against the deep blues below. */
export const BLUE_2 = '#80bbff';

/** `Color/Blue/9` — the deep blue that carries white text, for accents on a dark surface. */
export const BLUE_9 = '#002880';

/** `Color/Blue/10` — quieter than `Blue/9`; the informational surface on a dark panel. */
export const BLUE_10 = '#00184d';
